<template>
  <div class="room-selector" :set-options="options = create_options">
    <p class="room-selector__title">{{ selector_title }}</p>
    <div class="d-flex align-items-center">
      <div class="room-selector__price">
        <span
          v-if="
            product_info.product.deal && product_info.product.deal.tags.length
          "
          class="room-selector__deal"
        >
          Ottimo prezzo!
        </span>
        <span
          class="room-selector__value"
        >€ {{ options[1].value.live_price_no_tax }}</span>
        <span class="room-selector__tax">include gestione e assistenza</span>
      </div>
      <b-form-select
        v-model="
          model_products[product_info.room_index][product_info.product_index]
            .price
        "
        class="room-selector__select"
        :options="options"
        @change="
          solution({
            room_index: product_info.room_index,
            product_index: product_info.product_index,
          })
        "
      />
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import { string, min, add, subtract, multiply } from 'mathjs';
  import { markup, round_up } from '@bookingv3/utilities';

  export default {
    name: 'room-selector',

    components: {},

    inject: ['details'],

    props: {
      product_info: {
        type: Object,
        default: Object,
      },
    },

    data() {
      return {};
    },

    computed: {
      ...mapState({
        // stato globale
        occupancy: (state) => state.occupancy,

        // stato del modulo livebkv3
        model_products: (state) =>
          state.livebkv3.rooms_selection.model_products,
        product_quantities: (state) =>
          state.livebkv3.rooms_selection.product_quantities,

        selected_solution: (state) => state.livebkv3.selected_solution,
        suggested_selections: (state) => state.livebkv3.suggested_selections,
      }),

      // Titolo della select
      selector_title() {
        return this.get_config('guiSettings.occupancy.maxRooms', 1) > 1
          ? 'Quante camere?'
          : 'Scegli la camera';
      },

      // Genera le options delle select
      create_options() {
        const { product } = this.product_info;
        const max_rooms = this.get_config('guiSettings.maxRooms', 1);
        const options = [
          {
            value: {
              qty: 0,
              base: 0,
              book: 0,
              total: 0,
              live_price: 0,
              live_price_no_tax: 0,
              extra_charges: {},
            },
            text: '0',
          },
        ]; // default zero
        const available_quantity = min(
          product.number_available_at_this_price,
          this.occupancy.length || max_rooms
        );

        // determina se la option è disabilitata
        const is_option_disabled = (qty) =>
          this.product_quantities &&
          this.product_quantities.hasOwnProperty(product.room) &&
          qty > this.product_quantities[product.room][product.id];

        // crea la struttura dati per gli extra charges con il valore total_amount
        const create_extra_charges = (prod, qty) => {
          const { extra_charges } = prod.price;

          const parse_extra = (type) =>
            type
              .filter((item) => item.total_amount)
              .map((item) => ({
                charge: item.charge,
                total_amount: multiply(item.total_amount, qty),
              }));

          return Object.fromEntries(
            Object.entries(extra_charges).map(([key, value]) => [
              key,
              parse_extra(value),
            ])
          );
        };

        // struttura delle option(con rispettivi value) calcolata sulla base della quantità disponibile e
        // il numero massimo di camere(available_quantity)
        const get_option = (qty) => {
          const base = multiply(product.price.base, qty);
          const book = multiply(product.price.book, qty);
          const total = multiply(product.price.total, qty);
          const total_tax_free = multiply(product.price.total_tax_free, qty);
          const { live_price } = markup(book);
          const extra_charges = create_extra_charges(product, qty);
          const live_price_no_tax = round_up(markup(total_tax_free).live_price);

          return {
            value: {
              qty,
              base,
              book,
              total,
              total_tax_free,
              live_price,
              live_price_no_tax,
              extra_charges,
            },
            text: `${qty} (€ ${live_price_no_tax})`,
            disabled: is_option_disabled(qty),
          };
        };

        // genera le option
        for (let i = 1; i <= available_quantity; i++) {
          options.push(get_option(i));
        }

        return options;
      },
    },

    created() {
      this.markup = markup;

      window.eventBus.$off(
        'recommended:preselect_product',
        this.preselect_from_recommended
      );
      window.eventBus.$on(
        'recommended:preselect_product',
        this.preselect_from_recommended
      );
    },

    beforeDestroy() {
      window.eventBus.$off(
        'recommended:preselect_product',
        this.preselect_from_recommended
      );
    },

    // mounted() {
    // },

    methods: {
      preselect_from_recommended({ room_id, product_id, qty }) {
        const { product } = this.product_info;
        if (product.id !== product_id || product.room !== room_id) return;

        const option = this.create_options.find(
          (o) => o.value.qty === qty && !o.disabled
        );
        if (!option) return;

        this.model_products[this.product_info.room_index][
          this.product_info.product_index
        ].price = option.value;
        this.solution(
          {
            room_index: this.product_info.room_index,
            product_index: this.product_info.product_index,
          },
          { from_recommended: true }
        );
      },

      // Aggiorna le options delle select in base alle quantità scelte
      update_options(room, model_room) {
        const products_qty = {}; // traccia le quantità selezionate di ogni prodotto (per camera)
        const occupancy_length = this.occupancy.length;
        const update_quantities = this.product_quantities;

        // popola 'products_qty' in base alle quantità scelte dall'utente di ogni prodotto.
        room.products.forEach((product, index) => {
          const room_id = product.room;
          if (!products_qty[room_id]) {
            products_qty[room_id] = {};
          }
          // assegna la quantità selezionata di ogni prodotto (tramite id)
          products_qty[room_id][product.id] = model_room[index].price.qty;
        });

        // popola 'options_products' che determina le opzioni disponibili per ogni camera e prodotto.
        room.products.forEach((product) => {
          const room_id = product.room;
          if (!update_quantities[room_id]) {
            update_quantities[room_id] = {};
          }
          const available_qty = room.products[0].number_available_at_this_price;
          // imposta la quantità disponibile per ogni prodotto, limitandola alla capacità di occupancy length
          update_quantities[room_id][product.id] =
            available_qty > occupancy_length ? occupancy_length : available_qty;
        });

        // aggiorna 'options_products' in base alle quantità selezionate (contenute in 'products_qty')
        for (const room_id in update_quantities) {
          const room_options = update_quantities[room_id];
          for (const product_id in room_options) {
            if (products_qty[room_id]) {
              for (const qty_product_id in products_qty[room_id]) {
                if (product_id !== qty_product_id) {
                  room_options[product_id] = subtract(
                    room_options[product_id],
                    products_qty[room_id][qty_product_id]
                  );
                }
              }
            }
          }
        }

        return update_quantities;
      },

      // Gestisce la selezione, l'aggiornamento e la rimozione di prodotti nelle camere. Calcola i prezzi e aggiorna lo store
      update_solution(product) {
        // inizializza i prezzi di default
        const initialize_pricing = () => ({
          base: 0,
          book: 0,
          total: 0,
          total_tax_free: 0,
          live_price: 0,
          live_price_no_tax: 0,
          extra_charges: {},
        });

        // rimuove il prodotto dalla room
        const remove_product_from_room = (rooms, exist_room_index, id) => {
          if (exist_room_index !== -1) {
            const target_room = rooms[exist_room_index];
            delete target_room.products[id];
            if (Object.keys(target_room.products).length === 0)
              {rooms.splice(exist_room_index, 1);}
          }
        };

        // aggiunge il prodotto alla room
        const add_or_update_product = (
          rooms,
          exist_room_index,
          room,
          id,
          prod
        ) => {
          if (exist_room_index === -1) {
            rooms.push({
              id_room: room,
              name: this.details.rooms[this.product_info.room_index].name.it,
              products: { [id]: prod },
            });
          } else {
            rooms[exist_room_index].products[id] = prod;
          }
        };

        // parsing degli extra_charges
        const parse_charges = (extra, target) => {
          if (extra) {
            extra.forEach(({ charge, total_amount }) => {
              const existing = target.find((e) => e.charge === charge);
              if (existing)
                {existing.total_amount = string(
                  round_up(add(existing.total_amount, total_amount))
                );}
              else target.push({ charge, total_amount });
            });
          }
          return target;
        };

        // aggiornamento dei prezzi
        const update_pricing = (rooms, pricing, extra_charges_map) => {
          for (const room of rooms) {
            for (const product_id in room.products) {
              const selected_product = room.products[product_id].price_selected;
              const { extra_charges } = selected_product;

              for (const key in extra_charges) {
                if (!extra_charges_map[key]) extra_charges_map[key] = [];
                extra_charges_map[key] = parse_charges(
                  extra_charges[key],
                  extra_charges_map[key]
                );
              }

              pricing.base = string(
                round_up(add(pricing.base, selected_product.base))
              );
              pricing.book = string(
                round_up(add(pricing.book, selected_product.book))
              );
              pricing.total = string(
                round_up(add(pricing.total, selected_product.total))
              );
              pricing.total_tax_free = string(
                round_up(
                  add(pricing.total_tax_free, selected_product.total_tax_free)
                )
              );
              pricing.live_price = string(
                round_up(add(pricing.live_price, selected_product.live_price))
              );
              pricing.extra_charges = extra_charges_map;
              pricing.live_price_no_tax = string(
                round_up(markup(pricing.total_tax_free).live_price)
              );
            }
          }
        };

        // creazione della struttura dati e aggiornamento dello store

        const rooms = this.selected_solution.rooms || [];
        const pricing = initialize_pricing();
        const extra_charges_map = {};

        const { room, price_selected, id } = product;
        const { qty } = price_selected;

        const exist_room_index = rooms.findIndex((r) => r.id_room === room);

        if (qty === 0) remove_product_from_room(rooms, exist_room_index, id);
        else if (qty > 0)
          {add_or_update_product(rooms, exist_room_index, room, id, product);}

        update_pricing(rooms, pricing, extra_charges_map);

        this.$store.commit('livebkv3/update_selected_solution', {
          rooms,
          pricing,
        });
      },

      // Soluzione scelta
      solution(ids, options = {}) {
        const model_room = this.model_products[ids.room_index];
        const model_product = model_room[ids.product_index];
        const room = this.details.rooms[ids.room_index];
        const product = room.products[ids.product_index];

        this.$store.commit('livebkv3/update_rooms_selection', {
          product_quantities: this.update_options(room, model_room),
        });

        this.update_solution({
          ...product,
          ...{ price_selected: model_product.price },
        });

        this.track_suggestions(
          product,
          model_product.price.qty,
          options.from_recommended
        );

        // numero totale di stanze selezionate e costo complessivo
        const selected_rooms = this.selected_solution.rooms || [];
        const room_count = selected_rooms.reduce(
          (total, sel_room) =>
            total +
            Object.values(sel_room.products).reduce(
              (sum, p) => sum + p.price_selected.qty,
              0
            ),
          0
        );
        const total_price =
          Number(this.selected_solution.pricing.live_price_no_tax) || 0;

        this.track_form({
          event: 'room-selector:select',
          data: { type: 'info', room_count, total_price },
        });
      },

      // Traccia le stanze provenienti dalle soluzioni consigliate.
      // Al click sulla soluzione consigliata fissa il baseline (qty preselezionata);
      // le deselezioni manuali successive possono solo far scendere il valore (ratchet).
      track_suggestions(product, qty, from_recommended) {
        const key = `${product.room}:${product.id}`;
        const tracked = { ...this.suggested_selections };

        if (from_recommended) {
          tracked[key] = qty;
        } else if (key in tracked) {
          tracked[key] = Math.min(tracked[key], qty);
        } else {
          return; // prodotto non consigliato: nessun impatto sul conteggio
        }

        this.$store.commit('livebkv3/suggested_selections', tracked);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .room-selector {
    position: relative;
    margin-top: 15px;

    @include media-breakpoint-up(md) {
      margin-top: 0;
    }

    &__title {
      font-size: $font-size-md;
      margin-bottom: 5px;
      text-align: right;

      @include media-breakpoint-up(md) {
        margin-bottom: $grid-gutter-width/3;
      }
    }

    &__price {
      display: flex;
      flex-direction: column;
      flex-basis: 60%;
      text-align: right;
      margin-right: $grid-gutter-width/2;

      @include media-breakpoint-up(sm) {
        flex-basis: 100%;
      }
    }

    &__deal {
      font-weight: normal;
      font-size: $font-size-md;
      color: $primary;
      margin: -10px 0 -7px;
    }

    &__value {
      font-size: $font-size-xl;
      color: $primary;
      font-weight: $vb-font-weight-semibold;
    }

    &__tax {
      font-size: $font-size-sm;
      color: $gray-500;
      cursor: default;
      line-height: normal;
    }

    &__select {
      flex-basis: 50%;
    }
  }
</style>
